import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import "./styles/global.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import {
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import { SearchProvider } from "./context/Search/SearchContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import useSettings from "./hooks/useSettings";

const defaultLogoLight = "/logo-light.png";
const defaultLogoDark = "/logo-dark.png";
const defaultLogoFavicon = "/favicon.png";
const darkCanvas = "#05070c";
const darkSurface = "#0b0d12";
const darkDialogSurface = "#121212";
const darkSurfaceElevated = "#11141b";
const darkSurfaceHover = "#171b24";
const darkBorder = "#202632";

const normalizeSettingValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof value.value === "string") return value.value;
  return "";
};

const buildPublicFileUrl = (value) => {
  const normalizedValue = normalizeSettingValue(value);
  if (!normalizedValue) return "";
  if (normalizedValue.startsWith("http") || normalizedValue.startsWith("/")) {
    return normalizedValue;
  }
  return `${getBackendUrl()}/public/${normalizedValue}`;
};

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#34637C";
  const sidebarTextColorLightLocalStorage = localStorage.getItem("sidebarTextColorLight") || "#ffffff";
  const sidebarTextColorDarkLocalStorage = localStorage.getItem("sidebarTextColorDark") || "#ffffff";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [sidebarTextColorLight, setSidebarTextColorLight] = useState(sidebarTextColorLightLocalStorage);
  const [sidebarTextColorDark, setSidebarTextColorDark] = useState(sidebarTextColorDarkLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState("");
  const [appLogoDark, setAppLogoDark] = useState("");
  const [appLogoLoginLight, setAppLogoLoginLight] = useState("");
  const [appLogoLoginDark, setAppLogoLoginDark] = useState("");
  const [appSidebarIconLight, setAppSidebarIconLight] = useState("");
  const [appSidebarIconDark, setAppSidebarIconDark] = useState("");
  const [appLogoFavicon, setAppLogoFavicon] = useState("");
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();
  // Estado para controlar o prompt de instalação do PWA
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        document.body.classList.add("theme-transitioning");
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode);
          return newMode;
        });
        setTimeout(() => {
          document.body.classList.remove("theme-transitioning");
        }, 400);
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setSidebarTextColorLight,
      setSidebarTextColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoLoginLight,
      setAppLogoLoginDark,
      setAppSidebarIconLight,
      setAppSidebarIconDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoLoginLight,
      appLogoLoginDark,
      appSidebarIconLight,
      appSidebarIconDark,
      appLogoFavicon,
      appName,
      sidebarTextColorLight,
      sidebarTextColorDark,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appLogoLoginLight, appLogoLoginDark, appName, appSidebarIconLight, appSidebarIconDark, sidebarTextColorLight, sidebarTextColorDark, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: 7,
              height: 7,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "rgba(140,120,100,0.3)" : "rgba(255,255,255,0.2)",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: mode === "light" ? "rgba(140,120,100,0.5)" : "rgba(255,255,255,0.35)",
            },
            "&::-webkit-scrollbar-button:single-button": {
              display: "block",
              height: 14,
              width: 7,
              background: "transparent",
              backgroundSize: "8px 8px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            },
            "&::-webkit-scrollbar-button:single-button:vertical:decrement": {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 1L1 6h6z' fill='${mode === "light" ? "rgba(140%2C120%2C100%2C0.35)" : "rgba(255%2C255%2C255%2C0.2)"}'/%3E%3C/svg%3E")`,
            },
            "&::-webkit-scrollbar-button:single-button:vertical:increment": {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 7L1 2h6z' fill='${mode === "light" ? "rgba(140%2C120%2C100%2C0.35)" : "rgba(255%2C255%2C255%2C0.2)"}'/%3E%3C/svg%3E")`,
            },
          },
          // ========================================================================
          // DESIGN SYSTEM GLOBAL (chatiaDS)
          // Padrão visual único do sistema. Qualquer componente usa theme.chatiaDS.*
          // Fonte de verdade: ContactDrawer (drawer) + ContactModal (dialog)
          // ========================================================================
          chatiaDS: {
            sidebar: {
              textColor: mode === "light" ? sidebarTextColorLight : sidebarTextColorDark,
              mutedTextColor:
                mode === "light"
                  ? sidebarTextColorLight
                  : sidebarTextColorDark,
            },

            // =====================================================================
            // A) HEADER — barra superior de drawers E dialogs (mesmo visual)
            //    Usa a cor primary do whitelabel (= cor da sidebar) pra dar
            //    coesão visual entre todos os modais/popovers e o menu lateral.
            // =====================================================================
            header: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 16px",
              background: mode === "light" ? primaryColorLight : primaryColorDark,
              color: "#ffffff",
              borderBottom: "none",
              flexShrink: 0,
            },
            headerTitle: {
              fontWeight: 600,
              fontSize: 15,
              color: "inherit",
            },

            // =====================================================================
            // B) BOTÃO FECHAR (X vermelho) — inline styles + hover handlers
            // =====================================================================
            closeButton: {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              color: "#ffffff",
              transition: "all 0.2s ease",
            },
            closeButtonHover: {
              backgroundColor: "rgba(255, 255, 255, 0.18)",
              color: "#ffffff",
            },

            // =====================================================================
            // C) DIALOG (modal) — Paper, content scroll, bordas, inputs internos
            // =====================================================================
            dialogPaper: {
              borderRadius: "21px 21px 12px 12px !important",
              maxHeight: "85vh !important",
              display: "flex !important",
              flexDirection: "column !important",
              overflow: "hidden !important",
              backgroundColor: mode === "light" ? "#ffffff !important" : `${darkDialogSurface} !important`,
              backgroundImage: "none !important",
              // form interno ocupa espaço disponível
              "& form": {
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              },
              // content rola, actions ficam fixas
              "& .MuiDialogContent-root": {
                flex: 1,
                overflowY: "auto !important",
                overflowX: "hidden",
              },
              "& .MuiDialogActions-root": {
                flexShrink: 0,
              },
              // bordas arredondadas globais dentro do dialog
              "& .MuiOutlinedInput-root": {
                borderRadius: 12,
              },
              "& .MuiButton-root": {
                borderRadius: 12,
              },
              "& .react-tel-input .form-control": {
                borderRadius: "12px !important",
              },
              "& .MuiSelect-icon": {
                right: 8,
              },
              // Autocomplete — inputs arredondados e indicadores
              "& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
                borderRadius: "12px !important",
                paddingRight: "56px !important",
              },
              "& .MuiAutocomplete-endAdornment": {
                right: "8px !important",
              },
              "& .MuiAutocomplete-popupIndicator": {
                padding: 4,
                color: mode === "light" ? "#667781" : "#8696a0",
              },
              "& .MuiAutocomplete-clearIndicator": {
                padding: 4,
                color: mode === "light" ? "#667781" : "#8696a0",
              },
              // Datetime-local — input nativo estilizado
              "& input[type='datetime-local']": {
                fontFamily: "inherit",
                fontSize: 14,
              },
              "& input[type='datetime-local']::-webkit-calendar-picker-indicator": {
                filter: mode === "dark" ? "invert(0.8)" : "none",
                cursor: "pointer",
                opacity: 0.6,
                padding: 2,
                borderRadius: 4,
              },
            },

            // =====================================================================
            // D) SELECT MENU (dropdown) — popup de selects em qualquer lugar
            // =====================================================================
            selectMenu: {
              borderRadius: "12px !important",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08) !important",
              "& .MuiList-root": {
                padding: 4,
              },
              "& .MuiMenuItem-root": {
                borderRadius: 7,
                fontSize: 14,
                "&:hover": {
                  backgroundColor: mode === "light" ? "#f5f0eb !important" : `${darkSurfaceElevated} !important`,
                },
                "&.Mui-selected": {
                  backgroundColor: mode === "light" ? "#ebe5df !important" : `${darkSurfaceHover} !important`,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: mode === "light" ? "#e0d8d0 !important" : `${darkBorder} !important`,
                },
              },
            },

            // =====================================================================
            // E) TABS (abas) — mesmo padrão em drawers e dialogs
            // =====================================================================
            tabs: {
              minHeight: 36,
              borderBottom: mode === "light" ? "1px solid #e8e8e8" : `1px solid ${darkBorder}`,
              "& .MuiTab-root": {
                minHeight: 36,
                textTransform: "none",
                fontSize: 13,
                fontWeight: 500,
                minWidth: 0,
                flex: 1,
              },
              "& .MuiTabs-indicator": {
                height: 2,
              },
            },

            // =====================================================================
            // F) SECTION CARD (cartão/bloco de conteúdo)
            // =====================================================================
            section: {
              margin: "8px 12px",
              padding: "14px 16px",
              borderRadius: 12,
              background: mode === "light" ? "#f7f8fa" : darkSurfaceElevated,
              border: mode === "light" ? "1px solid #e8e8e8" : `1px solid ${darkBorder}`,
            },
            sectionAccent: {
              margin: "8px 12px",
              padding: "14px 16px",
              borderRadius: 12,
              background: mode === "light" ? "#f0f7ff" : darkSurfaceElevated,
              border: mode === "light" ? "1px solid #c8ddf5" : "1px solid #253545",
            },
            sectionTitle: {
              fontSize: 13,
              fontWeight: 600,
              color: mode === "light" ? "#757575" : "#b0b0b0",
              marginBottom: 8,
              textTransform: "capitalize",
              letterSpacing: 0.3,
            },

            // =====================================================================
            // G) DIVIDER
            // =====================================================================
            divider: {
              height: 1,
              background: mode === "light" ? "#e8e8e8" : darkBorder,
              margin: "0 16px",
            },

            // =====================================================================
            // H) TOGGLE ROW (linha com switch on/off)
            // =====================================================================
            toggleItem: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
              borderRadius: 10,
              border: mode === "light" ? "1px solid #e0e0e0" : `1px solid ${darkBorder}`,
              marginBottom: 6,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: mode === "light" ? "#f5f5f5" : "#1e2d3a",
                borderColor: mode === "light" ? "#bdbdbd" : "#3a4f5c",
              },
            },
            toggleLabel: {
              fontSize: "0.9rem",
              color: mode === "light" ? "#424242" : "#d1d7db",
              fontWeight: 500,
            },

            // =====================================================================
            // I) FIELD ROW (linha label + valor — leitura)
            // =====================================================================
            fieldRow: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "8px 0",
              borderBottom: mode === "light" ? "1px solid #f0f0f0" : "1px solid #233039",
              "&:last-child": { borderBottom: "none" },
            },
            fieldLabel: {
              fontSize: 11,
              fontWeight: 600,
              color: mode === "light" ? "#8696a0" : "#8696a0",
              textTransform: "capitalize",
              letterSpacing: 0.3,
            },
            fieldValue: {
              fontSize: 14,
              color: mode === "light" ? "#111b21" : "#e9edef",
            },

            // =====================================================================
            // J) FORM FIELD ROW (linha de inputs lado a lado — edição)
            // =====================================================================
            formFieldRow: {
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 8,
            },

            // =====================================================================
            // K) INPUTS — bordas arredondadas padrão
            // =====================================================================
            inputOverrides: {
              "& .MuiOutlinedInput-root": {
                borderRadius: 12,
              },
              "& .MuiInputBase-input": {
                fontSize: 13,
              },
            },

            // =====================================================================
            // L) BUTTONS
            // =====================================================================
            actionBtn: {
              borderRadius: 12,
              textTransform: "none",
              fontSize: 12,
              fontWeight: 500,
              padding: "6px 16px",
            },
            deleteBtn: {
              color: "#e53935",
              padding: 6,
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#c62828 !important",
                backgroundColor: "rgba(229,57,53,0.08) !important",
              },
            },
            // Loading spinner sobre botão submit
            buttonProgress: {
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: -12,
              marginLeft: -12,
            },

            // =====================================================================
            // M) BADGE (pill) — tag colorida
            // =====================================================================
            badge: {
              display: "inline-block",
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 12,
              letterSpacing: 0.3,
            },

            // =====================================================================
            // N) CHIP (informação compacta — Conexão, Atendente, Carteira)
            // =====================================================================
            chip: {
              borderRadius: 50,
              fontWeight: 500,
              fontSize: "0.8em",
            },
            chipColors: {
              blue:   { backgroundColor: "#e3f2fd", color: "#1565c0" },
              green:  { backgroundColor: "#e8f5e9", color: "#2e7d32" },
              purple: { backgroundColor: "#f3e5f5", color: "#7b1fa2" },
              orange: { backgroundColor: "#fff3e0", color: "#e65100" },
              red:    { backgroundColor: "#fce4ec", color: "#c62828" },
              gray:   { backgroundColor: mode === "light" ? "#f1f5f9" : darkSurfaceElevated, color: mode === "light" ? "#475569" : "#8696a0" },
            },

            // =====================================================================
            // O) GREEN SWITCH — padrão do toggle on/off
            // =====================================================================
            greenSwitch: {
              root: {
                width: 36,
                height: 20,
                padding: 0,
                display: "flex",
              },
              switchBase: {
                padding: 2,
                color: "#fff !important",
                "&:hover": { backgroundColor: "transparent !important" },
              },
              switchBaseChecked: {
                transform: "translateX(16px)",
                color: "#fff !important",
                "&:hover": { backgroundColor: "transparent !important" },
              },
              trackChecked: {
                opacity: "1 !important",
                backgroundColor: "#4caf50 !important",
              },
              thumb: {
                width: 16,
                height: 16,
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                backgroundColor: "#fff !important",
              },
              track: {
                borderRadius: 10,
                opacity: "1 !important",
                backgroundColor: "#9e9e9e !important",
              },
            },

            // =====================================================================
            // P) PHONE INPUT — campo de telefone com bandeira
            // =====================================================================
            phoneInput: {
              "& .react-tel-input .form-control": {
                width: "100%",
                height: "40px",
                fontSize: "16px",
                borderRadius: 12,
                border: "1px solid rgba(0, 0, 0, 0.23)",
                "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)" },
              },
              "& .react-tel-input .country-list": {
                position: "fixed !important",
                zIndex: "2000 !important",
                borderRadius: "12px !important",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08) !important",
                maxHeight: "250px !important",
                padding: "4px !important",
                "& .search": {
                  padding: "8px !important",
                  "& .search-box": {
                    borderRadius: "12px !important",
                    width: "100% !important",
                    padding: "8px 12px !important",
                    fontSize: "14px !important",
                  },
                },
                "& .country": {
                  display: "flex !important",
                  alignItems: "center !important",
                  gap: "8px !important",
                  borderRadius: "7px !important",
                  padding: "8px 10px !important",
                  "&:hover": {
                    backgroundColor: (mode === "light" ? "#f5f0eb" : darkSurfaceElevated) + " !important",
                  },
                  "&.highlight": {
                    backgroundColor: (mode === "light" ? "#ebe5df" : darkSurfaceHover) + " !important",
                  },
                  "& .flag": {
                    position: "relative !important",
                    top: "auto !important",
                    marginRight: "0 !important",
                    flexShrink: 0,
                  },
                  "& .country-name": {
                    marginLeft: "0 !important",
                  },
                },
              },
            },

            // =====================================================================
            // Q) EMPTY STATE
            // =====================================================================
            empty: {
              textAlign: "center",
              padding: "60px 16px",
              color: mode === "light" ? "#999" : "#666",
              fontSize: 13,
              fontStyle: "italic",
            },

            // =====================================================================
            // R) CORES SEMÂNTICAS — paleta reutilizável
            // =====================================================================
            colors: {
              textPrimary: mode === "light" ? "#111b21" : "#e9edef",
              textSecondary: mode === "light" ? "#667781" : "#8696a0",
              textMuted: mode === "light" ? "#999" : "#666",
              border: mode === "light" ? "#e8e8e8" : darkBorder,
              borderLight: mode === "light" ? "#f0f0f0" : "#233039",
              borderAccent: mode === "light" ? "#d5d0cb" : darkBorder,
              surfaceDefault: mode === "light" ? "#f7f8fa" : darkSurfaceElevated,
              surfaceAccent: mode === "light" ? "#f0f7ff" : darkSurfaceElevated,
              surfaceHover: mode === "light" ? "#f3f4f6" : darkSurfaceHover,
              surfaceMenuHover: mode === "light" ? "#f5f0eb" : darkSurfaceElevated,
              surfaceMenuSelected: mode === "light" ? "#ebe5df" : darkSurfaceHover,
              danger: "#e53935",
              dangerHover: "#c62828",
              dangerBg: "rgba(229,57,53,0.08)",
              success: "#4caf50",
            },

            // =====================================================================
            // S) BORDER RADIUS — valores padrão
            // =====================================================================
            radius: {
              xs: 7,   // menu items
              sm: 10,  // toggles, pequenos cards
              md: 12,  // inputs, botões, cards, sections
              lg: 21,  // topo de dialogs
              full: 50, // chips, avatares
            },
          },
          // ========== FIM DESIGN SYSTEM ==========

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: 7,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "rgba(140,120,100,0.2)" : "rgba(255,255,255,0.15)",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: mode === "light" ? "rgba(140,120,100,0.4)" : "rgba(255,255,255,0.3)",
            },
            "&::-webkit-scrollbar-button:single-button": {
              display: "block",
              height: 14,
              width: 7,
              background: "transparent",
              backgroundSize: "8px 8px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            },
            "&::-webkit-scrollbar-button:single-button:vertical:decrement": {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 1L1 6h6z' fill='${mode === "light" ? "rgba(140%2C120%2C100%2C0.25)" : "rgba(255%2C255%2C255%2C0.15)"}'/%3E%3C/svg%3E")`,
            },
            "&::-webkit-scrollbar-button:single-button:vertical:increment": {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 7L1 2h6z' fill='${mode === "light" ? "rgba(140%2C120%2C100%2C0.25)" : "rgba(255%2C255%2C255%2C0.15)"}'/%3E%3C/svg%3E")`,
            },
          },
          palette: {
            type: mode,
            primary: { main: mode === "light" ? primaryColorLight : primaryColorDark },
            secondary: { main: "#ffffff" },
            textPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EEE" : darkSurface,
            optionsBackground: mode === "light" ? "#fafafa" : darkSurface,
            fancyBackground: mode === "light" ? "#fafafa" : darkCanvas,
            total: mode === "light" ? "#fff" : darkSurface,
            messageIcons: mode === "light" ? "grey" : "#F3F3F3",
            inputBackground: mode === "light" ? "#FFFFFF" : darkSurfaceElevated,
            barraSuperior: mode === "light" ? primaryColorLight : darkSurface,
            darkCanvas,
            darkSurface,
            darkDialogSurface,
            darkSurfaceElevated,
            darkSurfaceHover,
            darkBorder,
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoLoginLight,
          appLogoLoginDark,
          appSidebarIconLight,
          appSidebarIconDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            return appLogoDark || defaultLogoDark;
          },
          calculatedLogoLight: () => {
            return appLogoLight || defaultLogoLight;
          },
          calculatedLoginLogoDark: () => {
            return appLogoLoginDark || defaultLogoDark;
          },
          calculatedLoginLogoLight: () => {
            return appLogoLoginLight || defaultLogoLight;
          },
          calculatedSidebarIconDark: () => {
            return appSidebarIconDark || defaultLogoFavicon;
          },
          calculatedSidebarIconLight: () => {
            return appSidebarIconLight || defaultLogoFavicon;
          },
          overrides: {
            MuiCircularProgress: {
              root: {
                color: mode === "light" ? primaryColorLight : primaryColorDark,
              },
              svg: {
                strokeLinecap: "round",
              },
              circle: {
                strokeWidth: 2.5,
              },
            },
            MuiPaper: {
              outlined: {
                border: "none",
              },
            },
            // Padrão de botão do sistema: cor de destaque do whitelabel,
            // radius 12, sem caixa-alta, sombra leve.
            MuiButton: {
              root: {
                borderRadius: 12,
                textTransform: "none",
                fontWeight: 600,
              },
              contained: {
                boxShadow: mode === "dark"
                  ? "0 2px 8px rgba(0,0,0,0.35)"
                  : "0 2px 8px rgba(0,0,0,0.08)",
                "&:hover": {
                  boxShadow: mode === "dark"
                    ? "0 3px 12px rgba(0,0,0,0.45)"
                    : "0 3px 12px rgba(0,0,0,0.12)",
                  filter: "brightness(1.05)",
                },
              },
              containedPrimary: {
                background: mode === "light" ? primaryColorLight : primaryColorDark,
                color: "#fff",
                "&:hover": {
                  background: mode === "light" ? primaryColorLight : primaryColorDark,
                  filter: "brightness(1.05)",
                },
              },
            },
            MuiDialogActions: {
              root: {
                padding: "12px 16px",
              },
            },
            MuiDialog: {
              paper: {
                backgroundColor: mode === "light" ? "#ffffff" : darkDialogSurface,
                backgroundImage: "none",
              },
            },
            MuiAutocomplete: {
              paper: {
                borderRadius: 12,
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                background: mode === "dark" ? darkSurfaceElevated : "#ffffff",
                border: mode === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(0,0,0,0.06)",
              },
              listbox: {
                padding: 4,
                maxHeight: 280,
                "&::-webkit-scrollbar": {
                  width: 7,
                  height: 7,
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: mode === "light" ? "rgba(140,120,100,0.3)" : "rgba(255,255,255,0.2)",
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: mode === "light" ? "rgba(140,120,100,0.5)" : "rgba(255,255,255,0.35)",
                },
                "&::-webkit-scrollbar-button:single-button": {
                  display: "block",
                  height: 14,
                  width: 7,
                  background: "transparent",
                  backgroundSize: "8px 8px",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                },
                "&::-webkit-scrollbar-button:single-button:vertical:decrement": {
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 1L1 6h6z' fill='${mode === "light" ? "rgba(140%2C120%2C100%2C0.35)" : "rgba(255%2C255%2C255%2C0.2)"}'/%3E%3C/svg%3E")`,
                },
                "&::-webkit-scrollbar-button:single-button:vertical:increment": {
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 7L1 2h6z' fill='${mode === "light" ? "rgba(140%2C120%2C100%2C0.35)" : "rgba(255%2C255%2C255%2C0.2)"}'/%3E%3C/svg%3E")`,
                },
                scrollbarWidth: "thin",
                scrollbarColor: mode === "light"
                  ? "rgba(140,120,100,0.3) transparent"
                  : "rgba(255,255,255,0.2) transparent",
                "& .MuiAutocomplete-option": {
                  borderRadius: 7,
                  fontSize: 14,
                  "&[data-focus='true']": {
                    backgroundColor: mode === "light" ? "#f5f0eb" : darkSurfaceElevated,
                  },
                  "&[aria-selected='true']": {
                    backgroundColor: "rgba(6, 207, 156, 0.10)",
                  },
                },
              },
              popupIndicator: {
                "& svg": {
                  width: 14,
                  height: 14,
                },
              },
            },
            MuiPickersToolbar: {
              toolbar: {
                borderRadius: 0,
                background: mode === "light"
                  ? "linear-gradient(135deg, #f5f0eb 0%, #e0dcd7 100%)"
                  : `linear-gradient(135deg, ${darkSurfaceElevated} 0%, ${darkSurface} 100%)`,
                "& .MuiTypography-root": {
                  color: mode === "light" ? "#1a1a1a !important" : "#e9edef !important",
                },
                "& .MuiPickersToolbarText-toolbarTxt": {
                  color: mode === "light" ? "rgba(0,0,0,0.4) !important" : "rgba(255,255,255,0.4) !important",
                },
                "& .MuiPickersToolbarText-toolbarBtnSelected": {
                  color: mode === "light" ? "#1a1a1a !important" : "#fff !important",
                },
              },
            },
            MuiPickerDTHeader: {
              dateHeader: {
                "& .MuiPickersToolbarText-toolbarTxt": {
                  fontSize: "1.25rem !important",
                },
              },
              timeHeader: {
                "& .MuiPickersToolbarText-toolbarTxt": {
                  fontSize: "1.25rem !important",
                },
              },
            },
            MuiPickersDay: {
              day: { borderRadius: 8 },
              daySelected: {
                backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
                "&:hover": {
                  backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
                  opacity: 0.85,
                },
              },
              current: {
                color: mode === "light" ? primaryColorLight : primaryColorDark,
              },
            },
            MuiPickersYearSelection: {
              container: {
                "&::-webkit-scrollbar": {
                  width: 7,
                  height: 7,
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: mode === "light" ? "rgba(140,120,100,0.3)" : "rgba(255,255,255,0.2)",
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: mode === "light" ? "rgba(140,120,100,0.5)" : "rgba(255,255,255,0.35)",
                },
                "&::-webkit-scrollbar-button:single-button": {
                  display: "block",
                  height: 14,
                  width: 7,
                  background: "transparent",
                  backgroundSize: "8px 8px",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                },
                "&::-webkit-scrollbar-button:single-button:vertical:decrement": {
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 1L1 6h6z' fill='${mode === "light" ? "rgba(140%2C120%2C100%2C0.35)" : "rgba(255%2C255%2C255%2C0.2)"}'/%3E%3C/svg%3E")`,
                },
                "&::-webkit-scrollbar-button:single-button:vertical:increment": {
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M4 7L1 2h6z' fill='${mode === "light" ? "rgba(140%2C120%2C100%2C0.35)" : "rgba(255%2C255%2C255%2C0.2)"}'/%3E%3C/svg%3E")`,
                },
                scrollbarWidth: "thin",
                scrollbarColor: mode === "light"
                  ? "rgba(140,120,100,0.3) transparent"
                  : "rgba(255,255,255,0.2) transparent",
              },
            },
            MuiPickersYear: {
              root: {
                "&:focus": {
                  color: mode === "light" ? primaryColorLight : primaryColorDark,
                },
              },
              yearSelected: {
                color: mode === "light" ? primaryColorLight : primaryColorDark,
                fontWeight: 600,
              },
            },
            MuiPickersModal: {
              dialogRoot: {
                borderRadius: "16px !important",
                overflow: "hidden",
              },
              dialogAction: {
                "& .MuiButton-root": {
                  borderRadius: "12px !important",
                  minWidth: 80,
                  fontWeight: 600,
                  textTransform: "none",
                },
              },
            },
            MuiPickersClock: {
              pin: {
                backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
              },
            },
            MuiPickersClockPointer: {
              pointer: {
                backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
              },
              thumb: {
                border: `14px solid ${mode === "light" ? primaryColorLight : primaryColorDark}`,
              },
              noPoint: {
                backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
              },
            },
            MuiPickerDTTabs: {
              tabs: {
                backgroundColor: mode === "light"
                  ? "#e0dcd7"
                  : darkSurfaceElevated,
                "& .MuiTabs-indicator": {
                  backgroundColor: mode === "light" ? "#1a1a1a" : "#fff",
                },
                "& .MuiTab-root": {
                  color: mode === "light" ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.5)",
                  minHeight: 44,
                  "&.Mui-selected": {
                    color: mode === "light" ? "#1a1a1a" : "#fff",
                  },
                },
                "& .MuiTab-wrapper svg": {
                  display: "none",
                },
                "& .MuiTab-root:first-child .MuiTab-wrapper::before": {
                  content: "''",
                  display: "inline-block",
                  width: 20,
                  height: 20,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M16 2V6M8 2V6'/%3E%3Cpath d='M13 4H11C7.22876 4 5.34315 4 4.17157 5.17157C3 6.34315 3 8.22876 3 12V14C3 17.7712 3 19.6569 4.17157 20.8284C5.34315 22 7.22876 22 11 22H13C16.7712 22 18.6569 22 19.8284 20.8284C21 19.6569 21 17.7712 21 14V12C21 8.22876 21 6.34315 19.8284 5.17157C18.6569 4 16.7712 4 13 4Z'/%3E%3Cpath d='M3 10H21'/%3E%3Cpath d='M17 16.5C17 17.3284 16.3284 18 15.5 18C14.6716 18 14 17.3284 14 16.5C14 15.6716 14.6716 15 15.5 15C16.3284 15 17 15.6716 17 16.5Z'/%3E%3Cpath d='M15.5 15.5V17.5'/%3E%3C/svg%3E")`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                },
                "& .MuiTab-root:last-child .MuiTab-wrapper::before": {
                  content: "''",
                  display: "inline-block",
                  width: 20,
                  height: 20,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.5'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpath d='M12 8V12L14 14'/%3E%3C/svg%3E")`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                },
              },
            },
            MuiTooltip: {
              tooltip: {
                fontSize: 12,
                fontWeight: 500,
                padding: "8px 14px",
                borderRadius: 14,
                backgroundColor: "#525252",
                color: "#fafafa",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                letterSpacing: 0.3,
                opacity: 1,
              },
              arrow: {
                color: "#525252",
              },
            },
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appLogoLoginLight, appLogoLoginDark, appName, appSidebarIconLight, appSidebarIconDark, locale, mode, primaryColorDark, primaryColorLight, sidebarTextColorLight, sidebarTextColorDark]
  );

  // Tema MUI v5 espelhando o tema v4 — pra que TODOS os componentes @mui/material
  // (Selects, Buttons, etc.) tenham o mesmo visual usado em /reports e /dashboard,
  // sem precisar wrappar individualmente cada página.
  const v5Theme = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: mode === "light" ? "light" : "dark",
          primary: {
            main: mode === "light" ? primaryColorLight : primaryColorDark,
            dark: mode === "light" ? primaryColorLight : primaryColorDark,
            contrastText: "#ffffff",
          },
          background: {
            default: mode === "light" ? "#ffffff" : "#121212",
            paper: mode === "light" ? "#ffffff" : "#121212",
          },
          divider: mode === "light" ? "rgba(15,23,42,0.10)" : "rgba(255,255,255,0.10)",
        },
        components: {
          MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 12 } } },
          MuiButton: { styleOverrides: { root: { borderRadius: 12 } } },
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === "light" ? "#ffffff" : "#121212",
                backgroundImage: "none",
              },
            },
          },
        },
      }),
    [mode, primaryColorLight, primaryColorDark]
  );

  // Detecta quando o navegador está pronto para mostrar o prompt de instalação do PWA
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Previne o comportamento padrão do navegador
      e.preventDefault();
      // Armazena o evento para uso posterior
      setDeferredPrompt(e);
      
      // Mostra o prompt de instalação imediatamente
      setTimeout(() => {
        showInstallPrompt();
      }, 2000); // Pequeno delay para garantir que a página já carregou
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Função para mostrar o prompt de instalação
  const showInstallPrompt = () => {
    if (deferredPrompt) {
      // Verifica se o PWA já está instalado
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        // Mostra o prompt de instalação
        deferredPrompt.prompt();
        
        // Espera pela resposta do usuário
        deferredPrompt.userChoice.then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
          } else {
          }
          // Limpa o prompt armazenado, só pode ser usado uma vez
          setDeferredPrompt(null);
        });
      }
    }
  };

  useEffect(() => {
    try {
      const i18nlocale = localStorage.getItem("i18nextLng") || "pt";
      const browserLocale = i18nlocale.substring(0, 2) + (i18nlocale.length > 3 ? i18nlocale.substring(3, 5) : "");

      if (browserLocale === "ptBR" || browserLocale === "pt") {
        setLocale(ptBR);
      }
    } catch (e) {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    // WhiteLabel: Carregar TODAS as configurações com cache-first + paralelização
    let isMounted = true;

    const loadWhiteLabelSettings = async () => {
      const settingsConfig = [
        { key: 'appName', setter: setAppName, defaultValue: 'ChatIA', cache: true },
        { key: 'primaryColorLight', setter: setPrimaryColorLight, defaultValue: '#34637C', cache: true },
        { key: 'primaryColorDark', setter: setPrimaryColorDark, defaultValue: '#5A6C9B', cache: true },
        { key: 'sidebarTextColorLight', setter: setSidebarTextColorLight, defaultValue: '#ffffff', cache: true },
        { key: 'sidebarTextColorDark', setter: setSidebarTextColorDark, defaultValue: '#ffffff', cache: true },
        { key: 'appLogoLight', setter: setAppLogoLight, defaultValue: "", cache: true, isFile: true },
        { key: 'appLogoDark', setter: setAppLogoDark, defaultValue: "", cache: true, isFile: true },
        { key: 'appLogoLoginLight', setter: setAppLogoLoginLight, defaultValue: "", cache: true, isFile: true },
        { key: 'appLogoLoginDark', setter: setAppLogoLoginDark, defaultValue: "", cache: true, isFile: true },
        { key: 'appSidebarIconLight', setter: setAppSidebarIconLight, defaultValue: "", cache: true, isFile: true },
        { key: 'appSidebarIconDark', setter: setAppSidebarIconDark, defaultValue: "", cache: true, isFile: true },
        { key: 'appLogoFavicon', setter: setAppLogoFavicon, defaultValue: "", cache: true, isFile: true }
      ];

      // ETAPA 1: Carregar cache PRIMEIRO (síncrono, instantâneo)
      settingsConfig.forEach(({ key, setter, defaultValue, cache, isFile }) => {
        if (cache) {
          const cachedValue = localStorage.getItem(key);
          if (cachedValue && cachedValue !== 'null' && cachedValue !== 'undefined') {
            const value = isFile && !cachedValue.startsWith('http') && !cachedValue.startsWith('/')
              ? getBackendUrl() + "/public/" + cachedValue
              : cachedValue;

            if (isMounted) {
              setter(value);
            }

            // Atualiza document.title imediatamente se for appName
            if (key === 'appName') {
              document.title = cachedValue;
            }

          }
        }
      });

      // ETAPA 2: Executar TODAS as chamadas API EM PARALELO

      const promises = settingsConfig.map(({ key }) =>
        getPublicSetting(key).catch(error => {
          return null;
        })
      );

      const results = await Promise.allSettled(promises);

      // ✅ ETAPA 3: Processar resultados e atualizar estados + cache
      results.forEach((result, index) => {
        const { key, setter, defaultValue, cache, isFile } = settingsConfig[index];

        if (result.status === 'fulfilled' && result.value !== null) {
          const rawValue = normalizeSettingValue(result.value) || defaultValue;
          let value = rawValue;

          // Processar arquivos (adicionar URL base se necessário)
          if (isFile) {
            value = buildPublicFileUrl(rawValue);
          }

          if (isMounted) {
            setter(value);
          }

          // Salvar no cache
          if (cache) {
            if (rawValue) {
              localStorage.setItem(key, rawValue);
            } else {
              localStorage.removeItem(key);
            }
          }

          // Atualizar document.title se for appName
          if (key === 'appName') {
            document.title = rawValue;
          }

        } else {
          // Se falhou, usar valor padrão (se não tiver cache)
          const cachedValue = localStorage.getItem(key);
          if (!cachedValue) {
            let value = defaultValue;
            if (isFile && !value.startsWith('http') && !value.startsWith('/')) {
              // defaultValue já deve ser um caminho relativo válido
              value = defaultValue;
            }
            if (isMounted) {
              setter(value);
            }
          }
        }
      });

    };

    loadWhiteLabelSettings();
    // Cache backendUrl para o manifest dinâmico no index.html
    localStorage.setItem("backendUrl", getBackendUrl());

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ WhiteLabel: useEffect reativo para atualizar document.title quando appName muda
  useEffect(() => {
    if (appName && appName !== 'null' && appName !== 'undefined') {
      document.title = appName;
    }
  }, [appName]);

  // ✅ WhiteLabel: Envia config ao Service Worker para manifest dinâmico + atualiza favicon/apple-icon
  useEffect(() => {
    const name = (appName && appName !== 'null' && appName !== 'undefined') ? appName : 'ChatIA';
    const cleanBackend = getBackendUrl().replace(/\/+$/, '');
    const rawFavicon = appLogoFavicon || "";
    const faviconFile = rawFavicon.startsWith(`${cleanBackend}/public/`)
      ? rawFavicon.replace(`${cleanBackend}/public/`, "")
      : rawFavicon;
    const cleanFav = faviconFile.replace(/^\/+/, '');
    const swFaviconFile = (faviconFile.startsWith("http") || cleanFav.endsWith("favicon.png")) ? "" : cleanFav;

    const faviconUrl = (faviconFile && faviconFile !== 'null' && !faviconFile.endsWith('favicon.png'))
      ? (faviconFile.startsWith("http") || faviconFile.startsWith("/") ? faviconFile : `${cleanBackend}/public/${cleanFav}`)
      : null;

    // Envia config ao service worker (ele intercepta /manifest.json e /pwa-icon-wl.png)
    const sendConfig = () => {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "PWA_CONFIG",
          backendUrl: getBackendUrl(),
          faviconFile: (swFaviconFile && swFaviconFile !== 'null') ? swFaviconFile : "",
          appName: name,
        });
      }
    };
    sendConfig();
    // Também envia quando o SW assume controle (pode ser novo SW)
    navigator.serviceWorker?.addEventListener("controllerchange", sendConfig);

    // Atualiza apple-touch-icon e favicon no DOM para iOS/browser
    if (faviconUrl) {
      const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleIcon) appleIcon.setAttribute("href", faviconUrl);
      const iconLink = document.querySelector('link[rel="icon"]');
      if (iconLink) iconLink.setAttribute("href", faviconUrl);
    }

    return () => {
      navigator.serviceWorker?.removeEventListener("controllerchange", sendConfig);
    };
  }, [appName, appLogoFavicon]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primaryColor", mode === "light" ? primaryColorLight : primaryColorDark);
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={appLogoFavicon ? (appLogoFavicon.startsWith("http") || appLogoFavicon.startsWith("/") ? appLogoFavicon : getBackendUrl() + "/public/" + appLogoFavicon) : defaultLogoFavicon} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <ThemeProviderV5 theme={v5Theme}>
            <QueryClientProvider client={queryClient}>
              <ActiveMenuProvider>
                <SearchProvider>
  <div className="app-root-container">
    <Routes />
  </div>
                </SearchProvider>
              </ActiveMenuProvider>
            </QueryClientProvider>
          </ThemeProviderV5>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
